// Legea nr. 169/2026, the Code of Land Use Planning, Urbanism and
// Construction (CATUC). In force since 25 August 2026; it repeals Laws
// 50/1991 and 350/2001. Monitorul Oficial, Part I, no. 661 of 10.VIII.2026.
//
// Everything here was read in the Monitorul Oficial text, not in a summary.
// That is what `verified: true` means. Quotes are kept short; the article is
// the reference.
import type { Source } from "./ro.ts";

const MO = "Legea 169/2026 (CATUC)";

/**
 * POT: "proiecția pe sol a volumului construcției, respectiv amprenta
 * edificată la nivelul solului, și, după caz, proiecția pe sol a elementelor
 * construite care depășesc această amprentă, inclusiv etajele în consolă,
 * balcoanele și alte elemente constructive permanente".
 *
 * The list that follows in the Annex (uncovered ground-floor terraces,
 * alleys, ground-level parking, pools, play areas, urban furniture, waste
 * platforms, EV chargers, temporary buildings) reads, by its grammar, as
 * *included*, which makes no sense for alleys and parking. It is almost
 * certainly meant as excluded. MDLPA has reportedly called it a material
 * error awaiting rectification. The engine treats it as excluded and says so.
 */
export const POT_SOURCE: Source = {
  act: MO, article: "Anexa nr. 1, „Procent de ocupare a terenului”", verified: true,
  note: "Include proiecția etajelor în consolă și a balcoanelor. Lista de suprafețe din anexă (terase, alei, parcări la sol…) e citită ca excludere: textul e ambiguu, se pare o eroare materială care așteaptă rectificare.",
};

/**
 * CUT: built area measured at the outside of the facade enclosures, less 14
 * listed surfaces. The ones this engine meets: basement and demi-basement
 * parking with ramps and manoeuvring (2), basement plant rooms (3), interior
 * courtyards (7), uncovered terraces (8), balconies and open loggias (10).
 */
export const CUT_SOURCE: Source = {
  act: MO, article: "Anexa nr. 1, „Coeficientul de utilizare a terenului”", verified: true,
  note: "Exclude balcoanele și logiile deschise, terasele neacoperite, parcarea din subsol cu rampele ei și curțile.",
};

/** "zonele cu CUT cu valoare mai mare de 4 se stabilesc numai prin Planul urbanistic general". */
export const CUT_ABOVE_4_SOURCE: Source = { act: MO, article: "art. 46 alin. (5)", verified: true };

/** The height regime counts the demi-basement, the mansard and a technical floor above the last functional level. */
export const HEIGHT_REGIME_SOURCE: Source = {
  act: MO, article: "Anexa nr. 1, „Regimul de înălțime”", verified: true,
  note: "Demisolul, mansarda și etajul tehnic de peste ultimul nivel se socotesc toate niveluri.",
};

/** Existing buildings that stay are added to the proposed ones before computing the indicators. */
export const EXISTING_SOURCE: Source = { act: MO, article: "Anexa nr. 1, „Indicatori urbanistici”", verified: true };

export type Derogation = "none" | "puz" | "puz-protected" | "pud";

/** How much each route can raise POT and CUT, once, over the approved values. */
export const DEROGATION: Record<Derogation, { factor: number; label: string; source: Source; condition: string }> = {
  none: { factor: 0, label: "Fără derogare", source: { act: MO, verified: true }, condition: "" },
  puz: {
    factor: 0.2, label: "PUZ: POT și CUT +20%",
    source: { act: MO, article: "art. 66 alin. (1) lit. e)", verified: true },
    condition: "PUZ inițiat de dezvoltator, cu studiu de oportunitate și aviz de inițiere; o singură dată, față de valoarea inițială.",
  },
  "puz-protected": {
    factor: 0.1, label: "PUZ în zonă protejată: +10%",
    source: { act: MO, article: "art. 66 alin. (1) lit. f)", verified: true },
    condition: "Doar fără a schimba caracterul zonei; o singură dată.",
  },
  pud: {
    factor: 0.2, label: "PUD: POT și CUT +20%",
    source: { act: MO, article: "art. 82 alin. (2) lit. b) pct. (i)", verified: true },
    condition: "Doar pentru a acoperi calcane vizibile, a alinia cornișa la vecini, a marca un colț sau capătul unei perspective, la stradă de categoria I–II, sau pentru reglementări tehnice; o singură dată.",
  },
};

/** Procedures started before 25.08.2026 stay under the law in force when they started. */
export const TRANSITION_SOURCE: Source = { act: MO, article: "art. 582", verified: true };

/**
 * HG 525/1996 (the RGU) and the other Government decisions adopted under the
 * repealed laws stay applicable until replaced (art. 583 alin. 1). So the
 * RGU parking annex still stands, as a default.
 */
export const RGU_STILL_APPLIES: Source = { act: MO, article: "art. 583 alin. (1)", verified: true };
